import {
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';

/* Small, dependency-free primitives styled by styles.css. */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  loading?: boolean;
};

export function Button({
  variant = 'primary',
  loading = false,
  disabled,
  children,
  className,
  ...rest
}: ButtonProps) {
  return (
    <button
      className={cx('btn', `btn--${variant}`, className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className="spinner spinner--inline" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

type TextFieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  /** Sits inside the input box at its right edge: an icon button such as the password toggle. */
  trailing?: ReactNode;
};

export function TextField({
  label,
  hint,
  error,
  trailing,
  id,
  className,
  ...input
}: TextFieldProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const control = (
    <input
      id={inputId}
      className="field__input"
      aria-invalid={error ? true : undefined}
      aria-describedby={cx(hint ? hintId : null, error ? errorId : null) || undefined}
      {...input}
    />
  );
  return (
    <div className={cx('field', error && 'field--invalid', className)}>
      <label className="field__label" htmlFor={inputId}>
        {label}
      </label>
      {trailing ? (
        <div className="field__control">
          {control}
          {trailing}
        </div>
      ) : (
        control
      )}
      {hint ? (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="field__error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

type PasswordFieldProps = Omit<TextFieldProps, 'type' | 'trailing'>;

/** A password input with an eye button that shows or hides what was typed. */
export function PasswordField(props: PasswordFieldProps) {
  const [visible, setVisible] = useState(false);
  const toggleLabel = visible ? 'Hide password' : 'Show password';
  return (
    <TextField
      {...props}
      type={visible ? 'text' : 'password'}
      trailing={
        <button
          type="button"
          className="field__toggle"
          aria-label={toggleLabel}
          title={toggleLabel}
          onClick={() => setVisible((v) => !v)}
        >
          <EyeIcon off={visible} />
        </button>
      }
    />
  );
}

function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
      {off ? <path d="M4 4l16 16" /> : null}
    </svg>
  );
}

type SelectFieldProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label: string;
  hint?: ReactNode;
};

export function SelectField({ label, hint, id, children, ...select }: SelectFieldProps) {
  const autoId = useId();
  const selectId = id ?? autoId;
  return (
    <div className="field">
      <label className="field__label" htmlFor={selectId}>
        {label}
      </label>
      <select id={selectId} className="field__input" {...select}>
        {children}
      </select>
      {hint ? <p className="field__hint">{hint}</p> : null}
    </div>
  );
}

export function Alert({
  kind = 'info',
  children,
}: {
  kind?: 'info' | 'success' | 'warn' | 'error';
  children: ReactNode;
}) {
  return (
    <div className={`alert alert--${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

export function Card({
  title,
  subtitle,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx('card', className)}>
      {title || actions ? (
        <header className="card__header">
          <div>
            {title ? <h2 className="card__title">{title}</h2> : null}
            {subtitle ? <p className="card__subtitle">{subtitle}</p> : null}
          </div>
          {actions ? <div className="card__actions">{actions}</div> : null}
        </header>
      ) : null}
      <div className="card__body">{children}</div>
    </section>
  );
}

/**
 * A group of content held together by whitespace and a heading row rather
 * than a border: the composition for tables and stat rows. `meta` is the
 * count or period next to the title; `actions` sit at the right. Card stays
 * for forms and anything that needs a boundary.
 */
export function Section({
  title,
  meta,
  actions,
  children,
  className,
  level = 2,
}: {
  title: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  level?: 2 | 3;
}) {
  const Heading = level === 3 ? 'h3' : 'h2';
  return (
    <section className={cx('section', className)}>
      <header className="section__head">
        <div className="section__text">
          <Heading className="section__title">{title}</Heading>
          {meta ? <p className="section__meta">{meta}</p> : null}
        </div>
        {actions ? <div className="section__actions">{actions}</div> : null}
      </header>
      {children}
    </section>
  );
}

/**
 * A modal on the native <dialog>: showModal() when `open` turns true, close()
 * when it turns false. The parent owns the state; Escape (the dialog's cancel
 * event) and a click on the backdrop ask it to close through onClose. The
 * first form control inside gets focus on open. Body scrolling is untouched.
 * A submit button in `footer` reaches a form in the body through its `form` id.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const backdropPress = useRef(false);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      dialog.querySelector<HTMLElement>('input, select, textarea')?.focus();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="modal"
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClose={() => {
        // Closed by the browser itself (a second Escape in Chrome): tell the parent.
        if (open) onClose();
      }}
      onMouseDown={(e) => {
        backdropPress.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        // A press that started and ended on the backdrop; a drag out of the panel does not count.
        if (backdropPress.current && e.target === e.currentTarget) onClose();
        backdropPress.current = false;
      }}
    >
      <div className="modal__panel">
        <header className="modal__header">
          <h2 className="modal__title" id={titleId}>
            {title}
          </h2>
        </header>
        <div className="modal__body">{children}</div>
        {footer ? <footer className="modal__footer">{footer}</footer> : null}
      </div>
    </dialog>
  );
}

export type Tone = 'neutral' | 'ok' | 'warn' | 'bad' | 'accent';

/**
 * A state word with its tone. The ok / warn / bad tones also draw a glyph
 * (check, exclamation, cross) so the state never rides on colour alone; the
 * text child is required for the same reason.
 */
export function Badge({
  tone = 'neutral',
  children,
  title,
}: {
  tone?: Tone;
  children: ReactNode;
  title?: string;
}) {
  return (
    <span className={`badge badge--${tone}`} title={title}>
      <BadgeGlyph tone={tone} />
      {children}
    </span>
  );
}

function BadgeGlyph({ tone }: { tone: Tone }) {
  const path =
    tone === 'ok'
      ? 'M2.5 8.5 6 12l7.5-8'
      : tone === 'warn'
        ? 'M8 3v6.5M8 12.5v.5'
        : tone === 'bad'
          ? 'M4 4l8 8M12 4l-8 8'
          : null;
  if (!path) return null;
  return (
    <svg
      className="badge__glyph"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      data-glyph={tone}
    >
      <path d={path} />
    </svg>
  );
}

/** Colour for the lifecycle statuses the hub uses (users and tenants). */
export function statusTone(status: string): Tone {
  switch (status) {
    case 'ACTIVE':
      return 'ok';
    case 'PENDING':
      return 'warn';
    case 'SUSPENDED':
    case 'INACTIVE':
    case 'CLOSED':
      return 'bad';
    default:
      return 'neutral';
  }
}

const CONSOLE_STATUS: Record<string, string> = {
  ACTIVE: 'Active',
  PENDING: 'Pending',
  INACTIVE: 'Inactive',
  SUSPENDED: 'Suspended',
  CLOSED: 'Closed',
};

const PORTAL_STATUS: Record<string, string> = {
  ACTIVE: 'Live',
  PENDING: 'Under review',
  INACTIVE: 'Inactive',
  SUSPENDED: 'Access paused',
  CLOSED: 'Closed',
};

/**
 * The word for a lifecycle status. The console gets short sentence-case
 * words; the portal interprets ("Live", "Under review", "Access paused").
 * Unknown statuses are lower-cased rather than shown as raw enum values.
 */
export function statusLabel(status: string, audience: 'console' | 'portal' = 'console'): string {
  const table = audience === 'portal' ? PORTAL_STATUS : CONSOLE_STATUS;
  if (table[status]) return table[status];
  const words = status.toLowerCase().replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * One figure with its reference. `unit` renders smaller and lighter after
 * the value; `reference` is the comparison a bare number lacks ("of 15",
 * "vs previous 30 days"); `trend` is an arrow glyph plus text, never colour
 * alone. `hint` is kept for the older, lighter second line.
 */
export function Stat({
  label,
  value,
  unit,
  reference,
  trend,
  hint,
}: {
  label: string;
  value: ReactNode;
  unit?: string;
  reference?: ReactNode;
  trend?: { direction: 'up' | 'down' | 'flat'; label: string };
  hint?: ReactNode;
}) {
  return (
    <div className="stat">
      <div className="stat__value">
        {value}
        {unit ? <span className="stat__unit">{unit}</span> : null}
      </div>
      <div className="stat__label">{label}</div>
      {reference ? <div className="stat__reference">{reference}</div> : null}
      {trend ? (
        <div className="stat__trend" data-direction={trend.direction}>
          <span className="stat__arrow" aria-hidden="true">
            {trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : '→'}
          </span>
          <span className="sr-only">
            {trend.direction === 'up' ? 'Up' : trend.direction === 'down' ? 'Down' : 'Flat'}:{' '}
          </span>
          {trend.label}
        </div>
      ) : null}
      {hint ? <div className="stat__hint">{hint}</div> : null}
    </div>
  );
}

/** Page title block: optional eyebrow, the title, a one-line description and actions on the right. */
export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="page-head">
      <div className="page-head__text">
        {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
        <h1>{title}</h1>
        {description ? <p className="page-head__desc">{description}</p> : null}
      </div>
      {actions ? <div className="page-head__actions">{actions}</div> : null}
    </header>
  );
}

export function PageLoader({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="loader" role="status">
      <span className="spinner" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

/*
 * formatDate and shortId moved to src/format.ts; they stay exported here
 * because pages outside this branch import them from components/ui.
 * EmptyState lives in components/states.tsx with the other states.
 */
export { formatDate, shortId } from '../format';
export { EmptyState } from './states';

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}
